import { useEffect, useState, useRef } from 'react';
import { Bell, CheckCircle2, Shield, Loader2 } from 'lucide-react';
import { audioAlerts } from '../../utils/audioAlerts';

const API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');

interface Props {
  sessionId: string;
  fcmToken: string;
  registrarPush: (tokenOverride?: string) => Promise<void>;
  onDone: () => void;
  errMsg: string;
}

type SubPhase = 'waiting' | 'notification' | 'verifying' | 'success' | 'countdown';

/**
 * PhaseEPushVerify - Fase E do bonding: recebe e verifica OTP via push.
 *
 * Fluxo:
 *  1. waiting: Aguarda notificacao (onMessage intercepta FCM foreground)
 *  2. notification: Exibe card estilo Android sobre overlay escuro
 *  3. verifying: Auto-confirma OTP no backend
 *  4. success: "Notificacoes Ativadas com Sucesso!" + check verde
 *  5. countdown: 3, 2, 1 -> onDone()
 */
export function PhaseEPushVerify({ sessionId, fcmToken, registrarPush, onDone, errMsg }: Props) {
  const [subPhase, setSubPhase] = useState<SubPhase>('waiting');
  const [pushData, setPushData] = useState<{ title: string; body: string; otp: string } | null>(null);
  const [countdownNum, setCountdownNum] = useState(3);
  const [localError, setLocalError] = useState('');
  const listenerRef = useRef(false);

  // ── Som de alerta: recebendo notificacao ──
  const playNotificationBeep = () => {
    audioAlerts.playRecebendoNotificacao();
  };

  // ── Registrar onMessage listener do FCM ──
  useEffect(() => {
    if (listenerRef.current) return;
    listenerRef.current = true;

    (async () => {
      try {
        const { getMessaging, onMessage } = await import('firebase/messaging');
        const { getApps } = await import('firebase/app');
        const app = getApps()[0];
        if (!app) return;

        const messaging = getMessaging(app);
        onMessage(messaging, (payload) => {
          console.log('[FCM Foreground] Mensagem recebida:', payload);
          const title = payload.notification?.title || 'Aluno Conectado';
          const body = payload.notification?.body || '';
          const otp = payload.data?.otp || '';

          // Tocar alerta sonoro
          playNotificationBeep();

          setPushData({ title, body, otp });
          setSubPhase('notification');
        });
        console.log('[FCM] Foreground listener ativo para Phase E');
      } catch (e) {
        console.warn('[FCM] Falha ao registrar listener:', e);
      }
    })();
  }, []);

  // ── COUNTDOWN timer ──
  useEffect(() => {
    if (subPhase !== 'countdown') return;
    if (countdownNum <= 0) {
      onDone();
      return;
    }
    const t = setTimeout(() => setCountdownNum(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [subPhase, countdownNum, onDone]);

  // ── Clique na notificacao: auto-verificar ──
  const handleNotificationClick = async () => {
    if (!pushData?.otp) return;
    setSubPhase('verifying');

    try {
      const res = await fetch(`${API_BASE}/security/session/${sessionId}/push-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp: pushData.otp }),
      });
      if (!res.ok) throw new Error('Codigo invalido');

      setSubPhase('success');
      // Apos 2s, inicia countdown
      setTimeout(() => setSubPhase('countdown'), 2000);
    } catch {
      setLocalError('Erro ao verificar codigo. Tente reenviar.');
      setSubPhase('waiting');
    }
  };

  // ══════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════

  // ── SUCESSO ──
  if (subPhase === 'success') {
    return (
      <div className="glass-card animate-fade-in-up" style={{
        textAlign: 'center', padding: '3rem 1.5rem', borderRadius: 24,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
          width: 200, height: 200, background: 'rgba(16, 185, 129, 0.15)',
          filter: 'blur(50px)', zIndex: 0,
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            width: 96, height: 96, borderRadius: '50%', margin: '0 auto 1.5rem',
            background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(5,150,105,0.1))',
            border: '2px solid rgba(16,185,129,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 30px rgba(16,185,129,0.2)',
            animation: 'phase-e-pop 0.5s ease-out',
          }}>
            <CheckCircle2 size={48} strokeWidth={1.5} style={{ color: '#10b981' }} />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#10b981', marginBottom: '0.5rem' }}>
            Notificacoes Ativadas!
          </h2>
          <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>
            Tudo pronto. Finalizando...
          </p>
        </div>
        <style>{`@keyframes phase-e-pop { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
      </div>
    );
  }

  // ── COUNTDOWN ──
  if (subPhase === 'countdown') {
    return (
      <div className="glass-card animate-fade-in-up" style={{
        textAlign: 'center', padding: '3rem 1.5rem', borderRadius: 24,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            fontSize: '5rem', fontWeight: 900, color: '#10b981',
            textShadow: '0 0 40px rgba(16,185,129,0.3)',
            animation: 'phase-e-count 0.6s ease-out',
            lineHeight: 1,
          }} key={countdownNum}>
            {countdownNum > 0 ? countdownNum : ''}
          </div>
          <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginTop: '1rem' }}>
            Redirecionando...
          </p>
        </div>
        <style>{`@keyframes phase-e-count { 0% { transform: scale(0.3); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }`}</style>
      </div>
    );
  }

  // ── OVERLAY NOTIFICACAO (subPhase === 'notification') ──
  // Renderizado em cima do card de espera
  return (
    <div style={{ position: 'relative' }}>
      {/* Card principal: Aguardando / Verificando */}
      <div className="glass-card animate-fade-in-up" style={{
        textAlign: 'center', padding: '2.5rem 1.5rem', borderRadius: 24,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
          width: 150, height: 150, background: 'rgba(59, 130, 246, 0.1)',
          filter: 'blur(40px)', zIndex: 0,
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', margin: '0 auto 1.5rem',
            background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(99,102,241,0.05))',
            border: '1px solid rgba(59,130,246,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 20px rgba(59,130,246,0.1)',
          }}>
            {subPhase === 'verifying' ? (
              <Loader2 size={36} strokeWidth={1.5} style={{ color: '#3b82f6', animation: 'spin .8s linear infinite' }} />
            ) : (
              <Bell size={36} strokeWidth={1.5} style={{ color: '#3b82f6', animation: 'phase-e-bell 2s ease-in-out infinite' }} />
            )}
          </div>

          <h2 style={{
            fontSize: '1.4rem', fontWeight: 800, marginBottom: '0.5rem',
            color: '#f8fafc', letterSpacing: '-0.02em',
          }}>
            {subPhase === 'verifying' ? 'Verificando...' : 'Aguardando Notificacao'}
          </h2>
          <p style={{ fontSize: '0.9rem', color: '#94a3b8', marginBottom: '2rem', lineHeight: 1.7 }}>
            {subPhase === 'verifying'
              ? 'Confirmando o codigo recebido...'
              : 'Enviamos uma notificacao push para este dispositivo. Ela aparecera aqui em instantes.'
            }
          </p>

          {subPhase === 'waiting' && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '0.75rem', borderRadius: 12,
              background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)',
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%', background: '#3b82f6',
                animation: 'phase-e-blink 1.5s ease-in-out infinite',
              }} />
              <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                Ouvindo notificacoes...
              </span>
            </div>
          )}

          {(localError || errMsg) && (
            <div style={{
              padding: '0.75rem', borderRadius: 10, marginTop: '1.5rem',
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              color: '#fca5a5', fontSize: '0.85rem',
            }}>
              {localError || errMsg}
            </div>
          )}

          {subPhase === 'waiting' && (
            <button
              onClick={() => registrarPush()}
              style={{
                marginTop: '1.5rem', background: 'none', border: 'none',
                color: '#64748b', fontSize: '0.85rem', cursor: 'pointer',
                padding: '8px', textDecoration: 'underline',
                transition: 'color 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.color = '#e2e8f0'}
              onMouseOut={(e) => e.currentTarget.style.color = '#64748b'}
            >
              Nao recebeu? Reenviar notificacao
            </button>
          )}
        </div>
      </div>

      {/* ── OVERLAY ESCURO + CARD NOTIFICACAO ── */}
      {subPhase === 'notification' && pushData && (
        <>
          {/* Backdrop escuro */}
          <div style={{
            position: 'fixed', inset: 0, zIndex: 9998,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(4px)',
            animation: 'phase-e-fade-in 0.3s ease-out',
          }} />

          {/* Card estilo Android */}
          <div
            onClick={handleNotificationClick}
            style={{
              position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)',
              zIndex: 9999, width: 'calc(100% - 32px)', maxWidth: 400,
              cursor: 'pointer',
              animation: 'phase-e-slide-down 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #1e293b, #0f172a)',
              borderRadius: 20, padding: '1.25rem',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)',
            }}>
              {/* Header: icone + app name + tempo */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginBottom: '0.75rem', paddingBottom: '0.75rem',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'linear-gradient(135deg, #7c3aed, #3b82f6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Shield size={14} style={{ color: '#fff' }} />
                </div>
                <span style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 600 }}>
                  Aluno Conectado
                </span>
                <span style={{ color: '#475569', fontSize: '0.7rem', marginLeft: 'auto' }}>
                  agora
                </span>
              </div>

              {/* Titulo */}
              <h3 style={{
                color: '#f8fafc', fontSize: '1rem', fontWeight: 700,
                margin: '0 0 6px', lineHeight: 1.3,
              }}>
                {pushData.title}
              </h3>

              {/* Corpo */}
              <p style={{
                color: '#cbd5e1', fontSize: '0.9rem', margin: '0 0 1rem',
                lineHeight: 1.4,
              }}>
                {pushData.body}
              </p>

              {/* CTA */}
              <div style={{
                padding: '0.6rem', borderRadius: 12, textAlign: 'center',
                background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)',
                color: '#6ee7b7', fontSize: '0.85rem', fontWeight: 700,
              }}>
                Toque para confirmar
              </div>
            </div>
          </div>
        </>
      )}

      {/* Animations */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes phase-e-bell {
          0%, 100% { transform: rotate(0deg); }
          10% { transform: rotate(14deg); }
          20% { transform: rotate(-14deg); }
          30% { transform: rotate(10deg); }
          40% { transform: rotate(-8deg); }
          50% { transform: rotate(0deg); }
        }
        @keyframes phase-e-blink {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        @keyframes phase-e-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes phase-e-slide-down {
          from { transform: translateX(-50%) translateY(-100%); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
